import React, { useCallback, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { ExportDocument, Sale } from "../../../system/Domain";
import SaleExportStatus from "./SaleExportStatus";
import SaleExportDocument from "./SaleExportDocument";
import SaleExportDocumentNew from "./SaleExportDocumentNew";
import _ from "lodash";

interface Props {
	sale: Sale
	orderId: number
}

const SaleExportCard = ({ sale, orderId }: Props) => {
	const [saleData, setSaleData] = useState(sale);
	const saleExport = saleData.orders.find(o => o.id === orderId)?.export;
	const exportProofDocument = saleExport?.documents.find((d) => d.documentType === "ExportProof") ||
		{
			documentType: "ExportProof",
			file: null
		};

	const arrivalDocument = saleExport?.documents.find((d) => d.documentType === "ArrivalConfirmation") ||
		{
			documentType: "ArrivalConfirmation",
			file: null
		};

	const kycDocument = saleExport?.documents.find((d) => d.documentType === "KycConfirmation") ||
		{
			documentType: "KycConfirmation",
			file: null
		};

	const mandatoryOtherDocument = saleExport?.isOtherDocumentMandatory
		? saleExport?.documents.find((d) => d.documentType === "Other") ||
		{
			documentType: "Other",
			file: null
		}
		: null;

	const disableNewOther = saleExport.documents.some((d) => d.documentType === "Other" && d != mandatoryOtherDocument && !d.file);

	const addOtherDocument = useCallback((d: ExportDocument) => {
		setSaleData((s) => {
			const e = _.findLast(s.orders, o => o.id === orderId)?.export;
			if (e) {
				e.documents.push(d);
			}
			return { ...s };
		});
	}, []);

	useEffect(() => {
		setSaleData(sale);
	}, [sale]);

	const canModifyDocuments = saleExport?.canChangeDocuments;

	return (
		<Box sx={{ opacity: saleExport.status === "Cancelled" ? 0.7 : "unset" }}>
			<Box width="100%">
				{saleExport && (
					<SaleExportStatus sale={saleData} saleExport={saleExport} setSale={setSaleData} />
				)}
				<Box mt={2}>
					<SaleExportDocument
						sale={saleData}
						document={exportProofDocument}
						documentHistory={saleExport?.documentsHistory.filter((dh) => dh.value.documentType === "ExportProof")}
						modify={canModifyDocuments}
						mandatory
						setSale={setSaleData}
						orderId={orderId}
					/>
				</Box>
				<Box mt={2}>
					<SaleExportDocument
						sale={saleData}
						document={arrivalDocument}
						documentHistory={saleExport?.documentsHistory.filter((dh) => dh.value.documentType === "ArrivalConfirmation")}
						modify={canModifyDocuments}
						mandatory
						setSale={setSaleData}
						orderId={orderId}
					/>
				</Box>
				<Box mt={2}>
					<SaleExportDocument
						sale={saleData}
						document={kycDocument}
						documentHistory={saleExport?.documentsHistory.filter((dh) => dh.value.documentType === "KycConfirmation")}
						modify={canModifyDocuments}
						mandatory
						setSale={setSaleData}
						orderId={orderId}
					/>
				</Box>
				<Box>
					{mandatoryOtherDocument && (
						(<Box mt={2}>
							<SaleExportDocument
								key={mandatoryOtherDocument.file?.filename || saleExport?.documents.indexOf(mandatoryOtherDocument)}
								sale={saleData}
								document={mandatoryOtherDocument}
								documentHistory={saleExport?.documentsHistory.filter((dh) => dh.value.documentType === "Other" && dh.value.filename == mandatoryOtherDocument.file?.filename)}
								modify={canModifyDocuments}
								mandatory
								setSale={setSaleData}
								orderId={orderId}
							/>
						</Box>)
					)}
					{saleExport.documents.filter((d) => d.documentType === "Other" && d != mandatoryOtherDocument).map(d =>
						<Box mt={2}>
							<SaleExportDocument
								key={d.file?.filename || saleExport?.documents.indexOf(d)}
								sale={saleData}
								document={d}
								documentHistory={saleExport?.documentsHistory.filter((dh) => dh.value.documentType === "Other" && dh.value.filename == d.file?.filename)}
								modify={canModifyDocuments}
								setSale={setSaleData}
								orderId={orderId}
							/>
						</Box>
					)}
				</Box>
				{canModifyDocuments && (
					<Box mt={2}>
						<SaleExportDocumentNew
							onNewDocument={addOtherDocument}
							disabled={disableNewOther}
						/>
					</Box>
				)}
			</Box>
		</Box>
	);
};

export default SaleExportCard;
